import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, CalculatorIcon, ClockIcon, FileTextIcon, PackageIcon, SparklesIcon, Trash2Icon } from 'lucide-react';
import { DictationPad } from '../components/mesterai/DictationPad';
import { Material } from '../types/pro';
import { MaterialPreset, QuoteLine, lineTotal, parseDictation, summarize } from '../utils/mesterAi';
interface MesterAIProps {
  /** The material catalogue, used to price what the parser hears. */
  materials: Material[];
  defaultHourlyRate: number;
}
function kr(value: number): string {
  return `${value.toLocaleString('da-DK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })} kr`;
}
export function MesterAI({
  materials,
  defaultHourlyRate
}: MesterAIProps) {
  const [transcript, setTranscript] = useState('');
  const [hourlyRate, setHourlyRate] = useState(String(defaultHourlyRate));
  const [lines, setLines] = useState<QuoteLine[] | null>(null);
  const [unparsed, setUnparsed] = useState<string[]>([]);
  const presets: MaterialPreset[] = useMemo(() => materials.map(material => ({
    id: material.id ?? material.itemNo,
    name: material.name,
    price: material.salesPrice
  })), [materials]);
  const breakdown = useMemo(() => lines ? summarize(lines, unparsed) : null, [lines, unparsed]);
  const handleCalculate = () => {
    const result = parseDictation({
      transcript,
      hourlyRate: parseFloat(hourlyRate) || 0,
      presets
    });
    setLines(result.lines);
    setUnparsed(result.unparsed);
  };
  const updateLine = (id: string, patch: Partial<QuoteLine>) => {
    setLines(current => current?.map(line => line.id === id ? {
      ...line,
      ...patch,
      needsPrice: patch.unitPrice !== undefined ? patch.unitPrice <= 0 : line.needsPrice
    } : line) ?? null);
  };
  const removeLine = (id: string) => {
    setLines(current => current?.filter(line => line.id !== id) ?? null);
  };
  const missingPrices = breakdown?.lines.filter(line => line.needsPrice).length ?? 0;
  return <div data-id="element-1012" data-mp="1hv1bhpa2qqbi">
      <div className="mb-8" data-id="element-1013" data-mp="1huszdzlrzp1d">
        <p className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-700" data-id="element-1014" data-mp="1d1q0dby8bnds">
          <SparklesIcon className="h-3.5 w-3.5" data-id="element-1015" data-mp="1rpt74nk3j33n" />
          Mester-AI
        </p>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-stone-900" data-id="element-1016" data-mp="1msuj8mr0zsdw">
          Indtal en opgave, få tilbuddet
        </h1>
        <p className="mt-2 max-w-2xl text-stone-500" data-id="element-1017" data-mp="20r16l8ycm9zb">
          Beskriv opgaven med din stemme. Mester-AI genkender timer og
          materialer, henter priserne fra dine standard materialer og lægger
          tilbuddet sammen.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_400px]" data-id="element-1018" data-mp="1f897b7nf2m2">
        {/* Input */}
        <div className="space-y-5" data-id="element-1019" data-mp="2d1s76yj2suw5">
          <DictationPad transcript={transcript} onTranscriptChange={setTranscript} data-id="element-1020" data-mp="2d2tt5y1mr1sn" />

          <div className="rounded-2xl border border-stone-200 bg-white p-5" data-id="element-1021" data-mp="2i2zeyxl7xu2t">
            <label htmlFor="mester-rate" className="block text-sm font-medium text-stone-700" data-id="element-1022" data-mp="249hv1bomefc9">
              Din timepris
            </label>
            <div className="mt-1.5 flex flex-wrap items-center gap-3" data-id="element-1023" data-mp="1uasp8ha00lcd">
              <input id="mester-rate" type="number" step="0.01" value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} className="w-40 rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-1024" data-mp="1zb6n56hvyewo" />
              <p className="text-sm text-stone-500" data-id="element-1025" data-mp="3jq8isobfvyh5">
                {presets.length > 0 ? `${presets.length} standard materialer klar til prissætning` : 'Ingen materialer endnu — tilføj dem under Materialer'}
              </p>
            </div>

            <button onClick={handleCalculate} disabled={transcript.trim().length === 0} className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-stone-900 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-200 disabled:text-stone-400" data-id="element-1026" data-mp="2l1ogen0ouuxr">
              <CalculatorIcon className="h-4 w-4" data-id="element-1027" data-mp="278f8yrwawjhv" />
              Beregn tilbud
            </button>
          </div>

          {/* Parsed lines */}
          {breakdown && <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white" data-id="element-1028" data-mp="1xdx4vlmz2lxj">
              <header className="flex flex-wrap items-center gap-3 border-b border-stone-100 px-5 py-4" data-id="element-1029" data-mp="1schjfsgvudem">
                <h2 className="min-w-0 flex-1 text-base font-semibold text-stone-900" data-id="element-1030" data-mp="1oc7e8i0wzr0t">
                  Tilbudslinjer
                </h2>
                {missingPrices > 0 && <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700" data-id="element-1031" data-mp="7tnh6fu03sxb">
                    <AlertTriangleIcon className="h-3.5 w-3.5" data-id="element-1032" data-mp="cs8k4n0n0qpg" />
                    {missingPrices} mangler pris
                  </span>}
              </header>

              {breakdown.lines.length === 0 ? <p className="px-5 py-10 text-center text-sm text-stone-500" data-id="element-1033" data-mp="3l9bqx4vky62l">
                  Mester-AI fandt ingen linjer. Prøv at nævne timer og
                  materialer med antal.
                </p> : <ul className="divide-y divide-stone-100" data-id="element-1034" data-mp="3v0g2dum1r8so">
                  {breakdown.lines.map(line => <li key={line.id} className="px-5 py-4" data-id="element-1035" data-mp="2xjr245cqwele">
                      <div className="flex items-start gap-3" data-id="element-1036" data-mp="32ilm3amg8973">
                        <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${line.kind === 'labour' ? 'bg-brand-50 text-brand-600' : 'bg-stone-100 text-stone-500'}`} data-id="element-1037" data-mp="32h0rjl04wq98">
                          {line.kind === 'labour' ? <ClockIcon className="h-[18px] w-[18px]" data-id="element-1038" data-mp="2bja0tm6m5q7a" /> : <PackageIcon className="h-[18px] w-[18px]" data-id="element-1039" data-mp="27iq3oim1iml5" />}
                        </span>

                        <div className="min-w-0 flex-1" data-id="element-1040" data-mp="2lisitx2yr1wp">
                          <input value={line.description} onChange={e => updateLine(line.id, {
                    description: e.target.value
                  })} aria-label="Beskrivelse" className="w-full border-none bg-transparent p-0 text-sm font-medium text-stone-900 outline-none focus:ring-0" data-id="element-1041" data-mp="2gj657mj83jnd" />
                          <p className="mt-0.5 truncate text-xs italic text-stone-400" data-id="element-1042" data-mp="14qnm6miw8ax4">
                            “{line.source}”
                          </p>
                        </div>

                        <button onClick={() => removeLine(line.id)} aria-label={`Fjern ${line.description}`} className="rounded-lg p-1.5 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-1043" data-mp="1xe1cw2w00a8s">
                          <Trash2Icon className="h-4 w-4" data-id="element-1044" data-mp="1smjdvkdjcjqg" />
                        </button>
                      </div>

                      <div className="mt-3 flex flex-wrap items-end gap-3 pl-12" data-id="element-1045" data-mp="2gbug76ajam6p">
                        <label className="w-24" data-id="element-1046" data-mp="2l4njdpqctmgo">
                          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-stone-400" data-id="element-1047" data-mp="l1mekthnbq8i">
                            Antal
                          </span>
                          <input type="number" step="0.5" value={line.quantity} onChange={e => updateLine(line.id, {
                    quantity: parseFloat(e.target.value) || 0
                  })} className="w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500" data-id="element-1048" data-mp="g930zwiykv3p" />
                        </label>
                        <span className="pb-2 text-sm text-stone-500" data-id="element-1049" data-mp="3et1jo90hw4xe">
                          {line.unit}
                        </span>
                        <label className="w-32" data-id="element-1050" data-mp="3apdchrfrzbco">
                          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-stone-400" data-id="element-1051" data-mp="1q95nmv2gu0li">
                            Pris pr. enhed
                          </span>
                          <input type="number" step="0.01" value={line.unitPrice} onChange={e => updateLine(line.id, {
                    unitPrice: parseFloat(e.target.value) || 0
                  })} className={`w-full rounded-lg border px-2.5 py-1.5 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500 ${line.needsPrice ? 'border-amber-300 bg-amber-50' : 'border-stone-200'}`} data-id="element-1052" data-mp="1z9u3zwfc0d1k" />
                        </label>
                        <p className="ml-auto pb-1.5 text-sm font-semibold text-stone-900" data-id="element-1053" data-mp="1ej34hh4ym5hg">
                          {kr(lineTotal(line))}
                        </p>
                      </div>
                    </li>)}
                </ul>}

              {breakdown.unparsed.length > 0 && <div className="border-t border-stone-100 bg-stone-50 px-5 py-4" data-id="element-1054" data-mp="1th597unq8x0a">
                  <p className="text-xs font-semibold uppercase tracking-wide text-stone-500" data-id="element-1055" data-mp="1xg4czbxq2nq5">
                    Ikke forstået
                  </p>
                  <ul className="mt-1.5 space-y-1" data-id="element-1056" data-mp="22dw4i4ugg7tt">
                    {breakdown.unparsed.map((clause, index) => <li key={index} className="text-sm italic text-stone-500" data-id="element-1057" data-mp="2c6laik78kthr" data-mpi={index}>
                        “{clause}”
                      </li>)}
                  </ul>
                </div>}
            </div>}
        </div>

        {/* Quote summary */}
        <div className="xl:sticky xl:top-24 xl:self-start" data-id="element-1058" data-mp="9j4x65amjooa">
          <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white" data-id="element-1059" data-mp="9kpmngi30hfa">
            <header className="bg-stone-900 px-5 py-4" data-id="element-1060" data-mp="5g8aa2e1x6ej">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/50" data-id="element-1061" data-mp="i6ovat5kuzw">
                Tilbud
              </p>
              <p className="mt-1 text-2xl font-bold text-white" data-id="element-1062" data-mp="3rqkk836cmb1r">
                {breakdown ? kr(breakdown.total) : '—'}
              </p>
              <p className="mt-0.5 text-xs text-white/60" data-id="element-1063" data-mp="o8apraeyign7">inkl. moms</p>
            </header>

            {breakdown ? <>
                <dl className="divide-y divide-stone-100" data-id="element-1064" data-mp="jgj1j6j9wn08">
                  {[{
                label: `Arbejdstid (${breakdown.labourHours.toLocaleString('da-DK')} t)`,
                value: kr(breakdown.labourTotal)
              }, {
                label: 'Materialer',
                value: kr(breakdown.materialTotal)
              }, {
                label: 'Subtotal',
                value: kr(breakdown.subtotal),
                strong: true
              }, {
                label: 'Moms 25 %',
                value: kr(breakdown.vat)
              }].map(row => <div key={row.label} className="flex items-center justify-between gap-3 px-5 py-3" data-id="element-1065" data-mp="2wmzcu6y5j4bs">
                      <dt className={`text-sm ${row.strong ? 'font-semibold text-stone-900' : 'text-stone-500'}`} data-id="element-1066" data-mp="2mqmciq83ps82">
                        {row.label}
                      </dt>
                      <dd className={`text-sm ${row.strong ? 'font-semibold text-stone-900' : 'text-stone-700'}`} data-id="element-1067" data-mp="6zhyoeuh4u4v">
                        {row.value}
                      </dd>
                    </div>)}
                </dl>

                <div className="border-t border-stone-100 p-5" data-id="element-1068" data-mp="uvv16wqursxo">
                  <button className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1069" data-mp="q31wg8vdwkll">
                    <FileTextIcon className="h-4 w-4" data-id="element-1070" data-mp="ztwltm3xg0b9" />
                    Opret tilbud
                  </button>
                  {missingPrices > 0 && <p className="mt-2 text-center text-xs text-amber-600" data-id="element-1071" data-mp="1dr1wjzel5ncu">
                      Udfyld de manglende priser først
                    </p>}
                </div>
              </> : <p className="px-5 py-10 text-center text-sm text-stone-500" data-id="element-1072" data-mp="3bj7bgoojpmv6">
                Indtal opgaven og tryk “Beregn tilbud” for at se summen.
              </p>}
          </div>
        </div>
      </div>
    </div>;
}